import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { AlertCircle, ArrowRightLeft, BotOff, CircleSlash, Eye, Hourglass, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { RecentlyPlacedOrder } from '@/lib/draft-order-client';
import type { OrderPaymentHoldCode } from '@/lib/orders-client';
import { PaymentHoldNotice } from '@/components/orders/payment-hold-notice';

interface StartOrderDialogProps {
  open: boolean;
  /** The AI is already paused on this conversation, so starting one pauses nothing new. */
  alreadyPaused: boolean;
  /** Branch the order will be placed for, when one is settled. Named so it is never a surprise. */
  branchName?: string;
  recentOrder?: RecentlyPlacedOrder;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The disclaimer in front of taking an order by hand.
 *
 * Starting one pauses the conversation's AI, and that is not a detail: the agent stops answering this
 * customer until someone hands it back. It is also the only safe arrangement — two writers on one cart
 * is exactly what the takeover exists to prevent — so the dialog explains the consequence rather than
 * offering a choice about it.
 *
 * Nothing is sent to the customer here. Saying so is worth the line: an operator who thinks starting
 * an order messages the customer will avoid it mid-conversation, which is precisely when they need it.
 */
export function StartOrderDialog({
  open,
  alreadyPaused,
  branchName,
  recentOrder,
  submitting,
  onCancel,
  onConfirm,
}: StartOrderDialogProps) {
  const { t } = useTranslation('conversations');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('cart.start.dialog.title')}</DialogTitle>
          <DialogDescription>{t('cart.start.dialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
            <BotOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="space-y-0.5">
              <p className="font-medium">
                {alreadyPaused ? t('cart.start.dialog.aiAlreadyPaused') : t('cart.start.dialog.aiWillPause')}
              </p>
              <p>{t('cart.start.dialog.aiPauseHint')}</p>
            </div>
          </div>

          {recentOrder && <RecentOrderNotice order={recentOrder} />}

          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            <li>{t('cart.start.dialog.silent')}</li>
            <li>{t('cart.start.dialog.prefill')}</li>
            {branchName && <li>{t('cart.start.dialog.branch', { name: branchName })}</li>}
          </ul>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            {t('cart.actions.cancel')}
          </Button>
          <Button type="button" loading={submitting} onClick={onConfirm}>
            {!submitting && <ShoppingCart className="h-3.5 w-3.5" />}
            {alreadyPaused ? t('cart.start.dialog.confirm') : t('cart.start.dialog.confirmWithPause')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * An order placed minutes ago that nobody has dealt with yet. A warning, never a gate: the operator
 * usually has the customer on the phone adding something they forgot, which is a perfectly good reason
 * to open a second cart — and they know that better than this rule does.
 */
export function RecentOrderNotice({ order }: { order: RecentlyPlacedOrder }) {
  const { t } = useTranslation('conversations');
  // The clock time rather than "N minutes ago": the notice sits in a panel that stays open, and a
  // relative figure would either go stale or have to re-render on a timer to stay honest.
  const time = format(new Date(order.placedAt), 'HH:mm');

  return (
    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-300">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="space-y-0.5">
        <p className="font-medium">
          {t(
            order.status === 'pending_payment'
              ? 'cart.start.recent.awaitingPayment'
              : order.status === 'confirmed'
                ? 'cart.start.recent.accepted'
                : 'cart.start.recent.awaiting',
            { id: order.orderId.slice(0, 8), time },
          )}
        </p>
        <p>{t('cart.start.recent.hint')}</p>
      </div>
    </div>
  );
}

/**
 * An order on this conversation still waiting for its online payment. Either the start context said so
 * (`recentOrder`), or the start itself was refused with `409 ORDER_AWAITING_PAYMENT` — whose payload
 * carries the amount and the link's deadline instead of the time it was placed.
 */
export interface AwaitingPaymentOrder {
  orderId: string;
  placedAt?: string;
  total?: number;
  currency?: string;
  paymentDeadlineAt?: string;
}

interface AwaitingPaymentBlockProps {
  order: AwaitingPaymentOrder;
  /** The last hold refusal on this order (cancel or switch while the charge settles). */
  holdCode?: OrderPaymentHoldCode;
  /** An action is loading the order or writing to it. */
  busy?: boolean;
  onViewOrder: () => void;
  onSwitchPayment: () => void;
  onCancelOrder: () => void;
}

/**
 * The customer has an order waiting for its payment, so a second cart is not an option — one open
 * order per customer while it awaits payment, on every surface. Unlike {@link RecentOrderNotice} this
 * is a gate: the start button is disabled, and the ways out of the unpaid order are offered here.
 */
export function AwaitingPaymentBlock({
  order,
  holdCode,
  busy,
  onViewOrder,
  onSwitchPayment,
  onCancelOrder,
}: AwaitingPaymentBlockProps) {
  const { t } = useTranslation('conversations');
  const id = order.orderId.slice(0, 8);
  const amount =
    order.total != null && order.currency ? `${order.currency} ${Number(order.total).toFixed(2)}` : undefined;

  return (
    <div
      role="alert"
      className="space-y-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300"
    >
      <div className="flex items-start gap-2">
        <Hourglass className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="space-y-0.5">
          <p className="font-medium">
            {amount
              ? t('cart.start.blocked.titleWithTotal', { id, total: amount })
              : t('cart.start.blocked.title', { id })}
          </p>
          {order.paymentDeadlineAt ? (
            <p>{t('cart.start.blocked.deadline', { time: format(new Date(order.paymentDeadlineAt), 'HH:mm') })}</p>
          ) : (
            order.placedAt && (
              <p>{t('cart.start.blocked.placedAt', { time: format(new Date(order.placedAt), 'HH:mm') })}</p>
            )
          )}
          <p>{t('cart.start.blocked.hint')}</p>
        </div>
      </div>

      {holdCode && <PaymentHoldNotice code={holdCode} className="bg-card dark:bg-card" />}

      <div className="flex flex-wrap gap-1.5">
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onViewOrder}>
          <Eye className="h-3.5 w-3.5" />
          {t('cart.start.blocked.viewOrder')}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onSwitchPayment}>
          <ArrowRightLeft className="h-3.5 w-3.5" />
          {t('cart.start.blocked.switchPayment')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={onCancelOrder}
          className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40 dark:hover:text-red-200"
        >
          <CircleSlash className="h-3.5 w-3.5" />
          {t('cart.start.blocked.cancelOrder')}
        </Button>
      </div>
    </div>
  );
}
